/**
 * The research pages, as HTML: the hub (/research) and one page per research
 * question (/research/models, /research/prompts, /research/confidence).
 *
 * The hub is a directory with the headline findings: each question gets a
 * short answer, one chart and a link to its own page, so it stays the same
 * length however large the record grows. The full figures, how to read them
 * and the limitations live on the question's own page.
 *
 * ONE SOURCE FOR TWO READERS. The browser renders this (ResearchHub.js,
 * ResearchTopic.js), and the build copies this file next to public/seo.js as
 * research-hub.js so the HTML crawlers receive is the same page, word for word.
 * It is CommonJS with no dependencies for that reason: Node loads it as it is.
 *
 * Every short answer is written from the figures, not about them in advance:
 * it changes when they do, it compares only records of THIN_RECORD graded
 * calls or more, and it states its numerator and denominator. Every rate
 * appears with the count behind it; a record resting on fewer calls is still
 * shown, drawn pale and marked as an insufficient sample.
 */

// Below this many graded calls a record is an insufficient sample: shown, drawn
// pale, marked, and left out of every comparison and short answer.
const THIN_RECORD = 10;

const esc = v => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const finite = v => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const pct = v => (finite(v) ? `${Math.round(Number(v) * 100)}%` : '—');
const signedPct = v => (finite(v) ? `${Number(v) >= 0 ? '+' : ''}${(Number(v) * 100).toFixed(2)}%` : '—');
const ofCount = (v, hit, total) => (total ? `${pct(v)} (${hit} of ${total})` : '—');
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// "Sep 22, 2026", in UTC, the same on the server and in any browser.
function day(value) {
  if (!value) return '';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value);
  return Number.isFinite(d.getTime()) ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}` : '';
}
const dateTag = value => (day(value) ? `<time datetime="${esc(value)}">${esc(day(value))}</time>` : '');

const HORIZON = { '1h': '1 hour', '4h': '4 hours', '1d': '1 day', '2d': '2 days', '7d': '1 week', '30d': '1 month', '180d': '6 months' };
const horizonName = h => HORIZON[h] || String(h || '');

function formatValue(value, format) {
  if (!finite(value)) return value === null || value === undefined ? '—' : String(value);
  if (format === 'percent') return pct(value);
  if (format === 'signedPercent') return signedPct(value);
  return String(value);
}

// ------------------------------------------------------------------ charts

function rateChart(rows, { id, title }) {
  const data = (rows || []).filter(r => r.graded > 0 && finite(r.accuracy));
  if (!data.length) return '';
  const W = 680, LABEL = 170, TRACK = 300, ROW = 26, GAP = 8, TOP = 34, PAD = 12;
  const H = TOP + data.length * (ROW + GAP) + PAD;
  const x0 = PAD + LABEL;
  const half = x0 + TRACK / 2;
  const bars = data.map((r, i) => {
    const y = TOP + i * (ROW + GAP);
    const thin = r.graded < THIN_RECORD;
    const w = Math.max(2, Math.round(TRACK * r.accuracy));
    const fill = r.accuracy >= 0.5 ? '#1e7e34' : '#c0392b';
    const name = r.name.length > 24 ? `${r.name.slice(0, 23)}…` : r.name;
    // A thin record's rate is printed small and grey, and says why: 100% of 4
    // must not read louder than 74% of 38.
    const value = thin
      ? `<tspan fill="#888">${esc(pct(r.accuracy))} (${r.right} of ${r.graded}) · insufficient sample</tspan>`
      : `${esc(pct(r.accuracy))} <tspan fill="#888">(${r.right} of ${r.graded})</tspan>`;
    return `<text x="${x0 - 8}" y="${y + ROW - 8}" text-anchor="end" font-size="12" fill="${thin ? '#888' : '#333'}">${esc(name)}</text>`
      + `<rect x="${x0}" y="${y}" width="${TRACK}" height="${ROW}" fill="#f2f2f2"/>`
      + `<rect x="${x0}" y="${y}" width="${w}" height="${ROW}" fill="${fill}" fill-opacity="${thin ? 0.25 : 1}"/>`
      + `<text x="${x0 + TRACK + 8}" y="${y + ROW - 8}" font-size="12" fill="#333">${value}</text>`;
  }).join('\n');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" role="img" aria-labelledby="${id}-svg" font-family="Arial, Helvetica, sans-serif">
<title id="${id}-svg">${esc(title)}</title>
<rect width="${W}" height="${H}" fill="#fff"/>
${bars}
<line x1="${half}" y1="${TOP - 8}" x2="${half}" y2="${H - PAD + 2}" stroke="#555" stroke-dasharray="3,3"/>
<text x="${half}" y="${TOP - 14}" text-anchor="middle" font-size="10" fill="#888">50% — a coin flip</text>
</svg>`;
}

const BUCKET_MID = { '<50': 45, '50-59': 55, '60-69': 65, '70-79': 75, '80-89': 85, '90+': 95 };

function calibrationChart(calibration, { id, title }) {
  const points = (calibration || []).filter(c => BUCKET_MID[c.bucket] !== undefined);
  if (!points.length) return '';
  const W = 640, H = 340, L = 56, R = 24, T = 24, B = 56;
  const X = v => L + ((v - 40) / 60) * (W - L - R);
  const Y = v => T + (1 - v / 100) * (H - T - B);
  const series = [
    { key: 'rangeCoverage', hit: 'rangeHits', total: 'rangeChecked', label: 'Close inside the range', color: '#2c6fbb' },
    { key: 'accuracy', hit: 'right', total: 'graded', label: 'Direction right', color: '#e67e22' },
  ];
  const grid = [0, 25, 50, 75, 100].map(v => `<line x1="${L}" y1="${Y(v)}" x2="${W - R}" y2="${Y(v)}" stroke="#eee"/><text x="${L - 8}" y="${Y(v) + 4}" text-anchor="end" font-size="11" fill="#888">${v}%</text>`).join('')
    + points.map(c => `<text x="${X(BUCKET_MID[c.bucket])}" y="${H - B + 18}" text-anchor="middle" font-size="11" fill="#888">${esc(c.bucket)}%</text>`).join('');
  // Points, not a line: a line drawn through a level resting on two forecasts
  // makes its swing look like a finding.
  const dots = series.map(s => points.filter(c => finite(c[s.key])).map(c => `<circle cx="${X(BUCKET_MID[c.bucket]).toFixed(1)}" cy="${Y(c[s.key] * 100).toFixed(1)}" r="${Math.min(9, 3 + Math.sqrt(c[s.total]) / 2).toFixed(1)}" fill="${s.color}" fill-opacity="${c[s.total] < THIN_RECORD ? 0.25 : 1}"><title>${esc(`${s.label}, ${c.bucket}% confidence: ${pct(c[s.key])} (${c[s.hit]} of ${c[s.total]})${c[s.total] < THIN_RECORD ? ', insufficient sample' : ''}`)}</title></circle>`).join('')).join('\n');
  const legend = series.map((s, i) => `<rect x="${L + i * 190}" y="${H - 22}" width="12" height="12" fill="${s.color}"/><text x="${L + i * 190 + 18}" y="${H - 12}" font-size="12" fill="#333">${esc(s.label)}</text>`).join('')
    + `<line x1="${L + 380}" y1="${H - 16}" x2="${L + 404}" y2="${H - 16}" stroke="#999" stroke-dasharray="4,3"/><text x="${L + 410}" y="${H - 12}" font-size="12" fill="#333">Confidence as stated</text>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" role="img" aria-labelledby="${id}-svg" font-family="Arial, Helvetica, sans-serif">
<title id="${id}-svg">${esc(title)}</title>
<rect width="${W}" height="${H}" fill="#fff"/>
${grid}
<line x1="${X(40)}" y1="${Y(40)}" x2="${X(100)}" y2="${Y(100)}" stroke="#999" stroke-dasharray="4,3"/>
${dots}
${legend}
</svg>`;
}

// Bars either side of zero, for an experiment's average result.
function signedChart(rows, { id, title }) {
  const data = (rows || []).filter(r => finite(r.value));
  if (!data.length) return '';
  const W = 640, LABEL = 200, TRACK = 300, ROW = 24, GAP = 8, TOP = 30, PAD = 12;
  const H = TOP + data.length * (ROW + GAP) + PAD;
  const x0 = PAD + LABEL;
  const zero = x0 + TRACK / 2;
  const max = Math.max(...data.map(r => Math.abs(r.value)), 1e-9);
  const bars = data.map((r, i) => {
    const y = TOP + i * (ROW + GAP);
    const w = Math.max(1, Math.round((Math.abs(r.value) / max) * (TRACK / 2)));
    const positive = r.value >= 0;
    const name = r.name.length > 28 ? `${r.name.slice(0, 27)}…` : r.name;
    return `<text x="${x0 - 8}" y="${y + ROW - 7}" text-anchor="end" font-size="12" fill="#333">${esc(name)}</text>`
      + `<rect x="${positive ? zero : zero - w}" y="${y}" width="${w}" height="${ROW}" fill="${positive ? '#1e7e34' : '#c0392b'}"/>`
      + `<text x="${x0 + TRACK + 8}" y="${y + ROW - 7}" font-size="12" fill="#333">${esc(signedPct(r.value))} <tspan fill="#888">(${esc(plural(r.n, 'run', 'runs'))})</tspan></text>`;
  }).join('\n');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" role="img" aria-labelledby="${id}-svg" font-family="Arial, Helvetica, sans-serif">
<title id="${id}-svg">${esc(title)}</title>
<rect width="${W}" height="${H}" fill="#fff"/>
<line x1="${zero}" y1="${TOP - 6}" x2="${zero}" y2="${H - PAD + 2}" stroke="#555"/>
<text x="${zero}" y="${TOP - 12}" text-anchor="middle" font-size="10" fill="#888">0</text>
${bars}
</svg>`;
}

// ------------------------------------------------------------------ short answers

const solidOf = rows => (rows || []).filter(r => r.graded >= THIN_RECORD && finite(r.accuracy));
const thinOf = rows => (rows || []).filter(r => r.graded > 0 && r.graded < THIN_RECORD);

function thinNote(rows, noun) {
  const thin = thinOf(rows).length;
  return thin ? ` ${plural(thin, `${noun} has`, `${noun}s have`)} fewer than ${THIN_RECORD} graded calls, an insufficient sample, and ${thin === 1 ? 'is' : 'are'} left out of this comparison.` : '';
}

function rateAnswer(rows, noun) {
  const solid = solidOf(rows);
  if (!solid.length) return `No ${noun} has ${THIN_RECORD} graded calls yet, so no comparison is drawn.${thinNote(rows, noun)}`;
  if (solid.length === 1) {
    const r = solid[0];
    return `Only one ${noun} has ${THIN_RECORD} or more graded calls so far: ${r.name}, right on direction ${ofCount(r.accuracy, r.right, r.graded)}. There is nothing yet to compare it with.${thinNote(rows, noun)}`;
  }
  const byRate = [...solid].sort((a, b) => b.accuracy - a.accuracy);
  const top = byRate[0], bottom = byRate[byRate.length - 1];
  const above = solid.filter(r => r.accuracy > 0.5).length;
  return `Among the ${solid.length} ${noun}s with ${THIN_RECORD} or more graded calls, ${top.name} called direction right most often: ${ofCount(top.accuracy, top.right, top.graded)}. `
    + `The lowest was ${bottom.name}, at ${ofCount(bottom.accuracy, bottom.right, bottom.graded)}; ${above} of the ${solid.length} did better than a coin flip.${thinNote(rows, noun)}`;
}

function confidenceAnswer(calibration) {
  const solid = (calibration || []).filter(c => c.graded >= THIN_RECORD && finite(c.accuracy));
  if (solid.length < 2) return `Not yet measurable: fewer than two confidence levels have ${THIN_RECORD} graded directional calls.`;
  const lo = solid[0], hi = solid[solid.length - 1];
  const rates = solid.map(c => c.accuracy);
  const spread = Math.max(...rates) - Math.min(...rates);
  const detail = `Direction right was ${ofCount(lo.accuracy, lo.right, lo.graded)} at ${lo.bucket}% stated confidence and ${ofCount(hi.accuracy, hi.right, hi.graded)} at ${hi.bucket}%, and between ${pct(Math.min(...rates))} and ${pct(Math.max(...rates))} at every level with ${THIN_RECORD} or more graded calls.`;
  if (spread < 0.1) return `Not clearly, in the current sample: a higher stated confidence did not come with more directions right. ${detail}`;
  if (hi.accuracy > lo.accuracy) return `Somewhat: the most confident forecasts were right on direction more often than the least confident. ${detail}`;
  return `No: the most confident forecasts were right on direction less often than the least confident. ${detail}`;
}

function experimentAnswer(rows) {
  const data = rows.filter(r => finite(r.value));
  if (!data.length) return 'No run of this experiment has settled yet.';
  const best = data.reduce((a, r) => (r.value > a.value ? r : a));
  const worst = data.reduce((a, r) => (r.value < a.value ? r : a));
  const ahead = data.filter(r => r.value > 0).length;
  const runs = data.map(r => r.n);
  return `${ahead} of ${data.length} model-and-horizon records averaged above zero. `
    + `The best is ${best.name} at ${signedPct(best.value)}; the weakest, ${worst.name} at ${signedPct(worst.value)}. `
    + `Each average rests on ${Math.min(...runs) === Math.max(...runs) ? plural(runs[0], 'run', 'runs') : `${Math.min(...runs)} to ${Math.max(...runs)} runs`}, so a single run can still move it.`;
}

// ------------------------------------------------------------------ building blocks

/**
 * Every prompt the site runs, one row each: members' single-asset research and
 * each mode's fixed prompt. They ask different questions and are scored on
 * different measures, so each row carries its own measure in words and nothing
 * here ranks one against another.
 *
 * A mode reports per model and horizon; its row is the run-weighted average of
 * those, which is what "across all its settled runs" means.
 */
function promptRows(stats) {
  const t = stats.totals || {};
  const member = {
    name: 'Member research',
    path: '/research#forecasts',
    asks: 'A price range and direction for one asset over a fixed horizon, researched by the model itself.',
    measure: 'Direction right; close inside the range',
    sample: `${plural(t.graded || 0, 'graded call', 'graded calls')} from ${t.settled || 0} settled forecasts`,
    result: t.graded ? `Direction right: ${ofCount(t.accuracy, t.right, t.graded)}; in range: ${ofCount(t.rangeCoverage, t.rangeHits, t.rangeChecked)}` : 'No graded calls yet',
    empty: !t.graded,
    thin: t.graded > 0 && t.graded < THIN_RECORD,
    rate: t.graded ? t.accuracy : null,
    rateLabel: 'Direction right',
    // The largest author's models: per-model figures are never pooled across
    // authors (see researchStats).
    models: (((stats.benches || [])[0] || {}).models || [])
      .filter(m => m.graded > 0)
      .map(m => ({ name: m.name, rate: m.accuracy, n: m.graded })),
    n: t.graded || 0,
    unit: ['call', 'calls'],
  };
  const modes = (stats.experiments || []).map((e) => {
    const cols = e.columns || [];
    const runsCol = cols.find(c => c.key === 'runs') || cols.find(c => c.format === 'count');
    const rateCol = cols.find(c => c.format === 'percent');
    const scoreCol = cols.find(c => c.format === 'signedPercent');
    const rows = (e.rows || []).filter(r => finite(runsCol && r[runsCol.key]) && Number(r[runsCol.key]) > 0);
    const runs = rows.reduce((n, r) => n + Number(r[runsCol.key]), 0);
    const weighted = (col) => {
      const w = rows.filter(r => finite(r[col.key]));
      const n = w.reduce((a, r) => a + Number(r[runsCol.key]), 0);
      return n ? w.reduce((a, r) => a + Number(r[col.key]) * Number(r[runsCol.key]), 0) / n : null;
    };
    const parts = [];
    if (rateCol && runs) parts.push(`${rateCol.label}: ${pct(weighted(rateCol))}`);
    if (scoreCol && runs) parts.push(`${scoreCol.label}: ${signedPct(weighted(scoreCol))}`);
    return {
      name: e.title,
      path: e.path,
      asks: e.description,
      measure: [rateCol, scoreCol].filter(Boolean).map(c => c.label).join('; '),
      sample: runs ? `${plural(runs, 'settled model run', 'settled model runs')}${runsCol.label && !/settled runs/i.test(runsCol.label) ? ` (${runsCol.label.toLowerCase()})` : ''}` : 'No settled runs yet',
      result: parts.length ? parts.join('; ') : '—',
      empty: !runs,
      thin: runs > 0 && runs < THIN_RECORD,
      rate: rateCol && runs ? weighted(rateCol) : null,
      // Per model across its horizons, run-weighted; a model reported under
      // two keys with one name is one model.
      models: rateCol ? [...rows.reduce((acc, r) => {
        const m = acc.get(r.model) || { name: r.model, sum: 0, n: 0 };
        if (finite(r[rateCol.key])) { m.sum += Number(r[rateCol.key]) * Number(r[runsCol.key]); m.n += Number(r[runsCol.key]); }
        return acc.set(r.model, m);
      }, new Map()).values()].filter(m => m.n).map(m => ({ name: m.name, rate: m.sum / m.n, n: m.n })) : [],
      rateLabel: rateCol ? rateCol.label : null,
      n: runs,
      unit: ['run', 'runs'],
    };
  });
  return [member, ...modes];
}

/**
 * One bar per prompt: the share of its calls that came good, on its own
 * measure, which the bar's label names. Not a ranking -- the measures differ --
 * but it is how each prompt has done overall, on one page. A prompt with no
 * such rate, or nothing settled, is a row of text rather than a missing bar.
 */
/**
 * Bars against 50%, each labelled with the measure it is on. Rows:
 * { name, rate, n, unit: [one, many], thin, empty, sub, emptyText }.
 * A row with no rate is a line of text rather than a missing bar.
 */
function measureChart(data, { id, title }) {
  if (!data.length) return '';
  const W = 720, LABEL = 210, TRACK = 250, ROW = 30, GAP = 28, TOP = 38, PAD = 12;
  const H = TOP + data.length * (ROW + GAP) + PAD;
  const x0 = PAD + LABEL, half = x0 + TRACK / 2;
  const bars = data.map((r, i) => {
    const y = TOP + i * (ROW + GAP);
    const name = r.name.length > 26 ? `${r.name.slice(0, 25)}…` : r.name;
    const label = `<text x="${x0 - 8}" y="${y + ROW - 9}" text-anchor="end" font-size="15" fill="${r.thin || r.empty ? '#888' : '#333'}">${esc(name)}</text>`;
    const sub = r.sub ? `<text x="${x0}" y="${y + ROW + 16}" font-size="13" fill="#888">${esc(r.sub)}</text>` : '';
    if (!finite(r.rate)) {
      return label + `<text x="${x0}" y="${y + ROW - 9}" font-size="15" fill="#888">${esc(r.emptyText || 'Nothing settled yet')}</text>`;
    }
    const w = Math.max(2, Math.round(TRACK * r.rate));
    const fill = r.rate >= 0.5 ? '#1e7e34' : '#c0392b';
    return label
      + `<rect x="${x0}" y="${y}" width="${TRACK}" height="${ROW}" fill="#f2f2f2"/>`
      + `<rect x="${x0}" y="${y}" width="${w}" height="${ROW}" fill="${fill}" fill-opacity="${r.thin ? 0.25 : 1}"><title>${esc(`${r.name}: ${pct(r.rate)}${r.sub ? ` — ${r.sub}` : ''}, ${plural(r.n, r.unit[0], r.unit[1])}`)}</title></rect>`
      // The 50% mark on this bar's track only, so it never runs through text.
      + `<line x1="${half}" y1="${y - 3}" x2="${half}" y2="${y + ROW + 3}" stroke="#555" stroke-dasharray="3,3"/>`
      + `<text x="${x0 + TRACK + 8}" y="${y + ROW - 9}" font-size="15" fill="#333">${esc(pct(r.rate))} <tspan fill="#888">(${esc(plural(r.n, r.unit[0], r.unit[1]))}${r.thin ? ' · insufficient sample' : ''})</tspan></text>`
      + sub;
  }).join('\n');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" role="img" aria-labelledby="${id}-svg" font-family="Arial, Helvetica, sans-serif">
<title id="${id}-svg">${esc(title)}</title>
<rect width="${W}" height="${H}" fill="#fff"/>
${bars}
<text x="${half}" y="${TOP - 16}" text-anchor="middle" font-size="13" fill="#888">50%</text>
</svg>`;
}

function promptChart(stats, opts) {
  return measureChart(promptRows(stats).map(r => ({
    ...r, sub: r.rateLabel, emptyText: r.empty ? 'Nothing settled yet' : 'No hit-rate measure — see the table',
  })), opts);
}

// A 95% interval around a rate that excludes 50%: clearly better or worse than
// a coin flip, not just on the right side of it. n is calls, or runs for a
// mode -- not its picks, which would narrow the interval.
const margin = r => 1.96 * Math.sqrt((r.rate * (1 - r.rate)) / r.n);
const clearOfChance = r => r.rate - margin(r) > 0.5 || r.rate + margin(r) < 0.5;

// A prompt's models with a solid record, best first.
const solidModels = r => (r.models || []).filter(m => m.n >= THIN_RECORD && finite(m.rate)).sort((a, b) => b.rate - a.rate);
const measureName = r => r.rateLabel.toLowerCase().replace('spy', 'SPY');

/**
 * Best and weakest model in each prompt, two bars apiece: how far apart the
 * AIs are when the prompt is held fixed. Only models with a solid record, and
 * only prompts with two of them.
 */
function modelSpreadChart(stats, opts) {
  const data = [];
  for (const r of promptRows(stats)) {
    const solid = solidModels(r);
    if (solid.length < 2) continue;
    [solid[0], solid[solid.length - 1]].forEach((m, i) => data.push({
      name: m.name, rate: m.rate, n: m.n, unit: r.unit,
      sub: `${i ? 'Weakest' : 'Best'} in ${r.name} · ${measureName(r)}`,
    }));
  }
  return measureChart(data, opts);
}

const MODELS_HOW_TO_READ = `For each prompt with at least two models on ${THIN_RECORD} or more calls or runs, its best and its weakest model on that prompt's own measure. The prompt is the same for both, so the gap between them is the difference the model made.`;

const PROMPT_HOW_TO_READ = `One bar per prompt: the share of its calls or picks that came good, on that prompt's own measure, named under the bar. Member research counts directional calls; each mode averages over its settled model runs. The measures differ, so compare a bar with 50% rather than with another bar. Pale bars rest on fewer than ${THIN_RECORD} calls or runs.`;

/**
 * The answer to the section's question, written from the figures.
 *
 * Whether a prompt made a difference is read as: did it do clearly better or
 * worse than chance on its own measure? "Clearly" is a 95% interval around its
 * rate, sqrt(p(1-p)/n) with n the calls or runs behind it, that excludes 50%.
 * For a mode n is its runs, not its picks, which widens the interval: a
 * conservative reading, and the right direction to err in.
 */
function promptsAnswer(stats) {
  const rows = promptRows(stats);
  const measured = rows.filter(r => !r.thin && !r.empty && finite(r.rate));
  const empty = rows.filter(r => r.empty);
  const thin = rows.filter(r => r.thin);
  const styles = solidOf((stats.styles || []).map(st => ({ ...st, name: st.label })));
  const above = measured.filter(r => r.rate - margin(r) > 0.5);
  const below = measured.filter(r => r.rate + margin(r) < 0.5);
  const named = r => `${r.name} (${pct(r.rate)}, ${r.rateLabel.toLowerCase().replace('spy', 'SPY')})`;

  if (!measured.length) {
    return `Too early to say: no prompt has ${THIN_RECORD} settled calls or runs yet, so none can be told apart from chance.`;
  }
  const lo = measured.reduce((a, r) => (r.rate < a.rate ? r : a));
  const hi = measured.reduce((a, r) => (r.rate > a.rate ? r : a));
  const range = measured.length > 1
    ? `every prompt with ${THIN_RECORD} or more settled calls or runs lands between ${pct(lo.rate)} (${lo.name}) and ${pct(hi.rate)} (${hi.name}) on its own measure`
    : `the one prompt with ${THIN_RECORD} or more settled calls or runs, ${named(measured[0])}, lands at ${pct(measured[0].rate)}`;

  let verdict;
  if (!above.length && !below.length) {
    verdict = `Not measurably, so far. ${range[0].toUpperCase()}${range.slice(1)} — close to the 50% a coin flip gives, and none is far enough from 50% for its sample to rule chance out. No prompt design has yet produced a clear edge.`;
  } else {
    const parts = [];
    if (above.length) parts.push(`${above.map(named).join(' and ')} did clearly better than chance`);
    if (below.length) parts.push(`${below.map(named).join(' and ')} did clearly worse`);
    const rest = measured.length - above.length - below.length;
    verdict = `In part. ${range[0].toUpperCase()}${range.slice(1)}. ${parts.join('; ')}${rest ? `, while the other ${rest} cannot yet be told apart from a coin flip` : ''}.`;
  }
  const caveat = ' The prompts ask different questions and are scored on different measures, so this says whether each design beats chance, not which design is best.';
  const within = styles.length > 1
    ? ` Within member research, ${rateAnswer((stats.styles || []).map(st => ({ ...st, name: st.label })), 'prompt style')}`
    : ` Within member research only the ${styles.length ? styles[0].name.split(' — ')[0].toLowerCase() : 'default'} style has ${THIN_RECORD} or more graded calls, so how a member prompt is built cannot be compared yet.`;
  const pending = [
    thin.length ? `${thin.map(r => r.name).join(' and ')} ${thin.length === 1 ? 'has' : 'have'} too few settled runs to count` : '',
    empty.length ? `${empty.map(r => r.name).join(' and ')} ${empty.length === 1 ? 'has' : 'have'} no settled run yet` : '',
  ].filter(Boolean).join('; ');
  return `${verdict}${caveat}${modelsAnswer(rows)}${within}${pending ? ` ${pending[0].toUpperCase()}${pending.slice(1)}.` : ''}`;
}

// The models within each prompt: best against weakest, and whether either is
// clear of chance. With the prompt held fixed, the gap is the model's.
function modelsAnswer(rows) {
  const count = (r, m) => plural(m.n, r.unit[0], r.unit[1]);
  const spreads = rows.map(r => ({ r, solid: solidModels(r) })).filter(x => x.solid.length >= 2);
  const short = rows.filter(r => !r.empty && solidModels(r).length < 2);
  if (!spreads.length) {
    return ` The models cannot be compared yet: no prompt has two models with ${THIN_RECORD} or more settled calls or runs.`;
  }
  const gapOf = x => x.solid[0].rate - x.solid[x.solid.length - 1].rate;
  const lines = spreads.map((x) => {
    const { r, solid } = x;
    const best = solid[0], worst = solid[solid.length - 1];
    const clear = solid.filter(clearOfChance);
    return `in ${r.name}, ${best.name} ${pct(best.rate)} (${count(r, best)}) against ${worst.name} ${pct(worst.rate)} (${count(r, worst)}), a gap of ${Math.round(gapOf(x) * 100)} points`
      + (clear.length ? `, with ${clear.map(m => `${m.name} clearly ${m.rate > 0.5 ? 'better' : 'worse'} than chance`).join(' and ')}` : ', though no model there is clearly apart from chance yet');
  });
  const widest = spreads.reduce((a, x) => (gapOf(x) > gapOf(a) ? x : a));
  const promptSpread = (() => {
    const rates = rows.filter(r => !r.thin && !r.empty && finite(r.rate)).map(r => r.rate);
    return rates.length > 1 ? Math.max(...rates) - Math.min(...rates) : 0;
  })();
  const lead = gapOf(widest) > promptSpread
    ? 'The models differ more than the prompts do.'
    : 'Within each prompt, the models differ too.';
  return ` ${lead} With the prompt held fixed: ${lines.join('; ')}.`
    + (spreads.length > 1 ? ` The widest gap is in ${widest.r.name}.` : '')
    + (short.length ? ` ${short.map(r => r.name).join(' and ')} ${short.length === 1 ? 'does' : 'do'} not yet have two models with ${THIN_RECORD} or more settled ${short[0].unit[1]} to compare.` : '');
}

function promptTable(stats) {
  return `<div class="hub-table"><table>
${head(['Prompt', 'What it asks', 'Scored on', 'Sample', 'Result so far'])}
<tbody>
${promptRows(stats).map(r => `<tr><th scope="row"><a href="${esc(r.path)}">${esc(r.name)}</a>${r.thin ? ' <small>(insufficient sample)</small>' : ''}${r.empty ? ' <small>(nothing settled yet)</small>' : ''}</th><td class="hub-wrap">${esc(r.asks)}</td><td class="hub-wrap">${esc(r.measure)}</td><td class="hub-wrap">${esc(r.sample)}</td><td class="hub-wrap">${esc(r.result)}</td></tr>`).join('\n')}
</tbody>
</table></div>`;
}

const METRIC_HEAD = ['Forecasts', 'Settled', 'Directional calls graded', 'Direction right', 'Close inside the range', 'Average score'];
const head = labels => `<thead><tr>${labels.map(l => `<th scope="col">${esc(l)}</th>`).join('')}</tr></thead>`;

function metricTable(first, rows, nameOf) {
  return `<div class="hub-table"><table>
${head([first, ...METRIC_HEAD])}
<tbody>
${rows.map(r => `<tr><th scope="row">${esc(nameOf(r))}${r.graded > 0 && r.graded < THIN_RECORD ? ' <small>(insufficient sample)</small>' : ''}</th><td>${esc(r.forecasts)}</td><td>${esc(r.settled)}</td><td>${esc(r.graded)}</td><td>${esc(ofCount(r.accuracy, r.right, r.graded))}</td><td>${esc(ofCount(r.rangeCoverage, r.rangeHits, r.rangeChecked))}</td><td>${esc(finite(r.averageScore) ? Number(r.averageScore).toFixed(1) : '—')}</td></tr>`).join('\n')}
</tbody>
</table></div>`;
}

function figure({ id, title, svg, howToRead }) {
  if (!svg) return '';
  return `<h3 class="hub-chart-title" id="${id}-title">${esc(title)}</h3>
<figure class="hub-figure" aria-labelledby="${id}-title">
${svg}
${howToRead ? `<figcaption><strong>How to read it.</strong> ${esc(howToRead)}</figcaption>` : ''}
</figure>`;
}

const answer = text => `<p class="hub-answer"><strong>Short answer.</strong> ${esc(text)}</p>`;
const measures = text => `<p class="hub-measures"><strong>What this measures.</strong> ${esc(text)}</p>`;
const more = (href, text) => `<p class="hub-more"><a href="${esc(href)}">${esc(text)} →</a></p>`;
const fullFigures = table => `<h3 class="hub-table-title">Full figures</h3>\n${table}`;
const limitations = items => `<h2 id="limitations">Limitations</h2>\n<ul>${items.map(i => `<li>${esc(i)}</li>`).join('')}</ul>`;

// The main bench: the author with the most public forecasts, whose record the
// hub summarises; every bench has its own table on /research/models.
const mainBench = stats => (stats.benches || [])[0] || null;

const RATE_HOW_TO_READ = `Each bar is the share of directional calls that were right, with the calls behind it in brackets. The dashed line is 50%, a coin flip. Pale bars marked "insufficient sample" rest on fewer than ${THIN_RECORD} graded calls. Listed by how many calls were made, not by rank.`;
const CALIBRATION_HOW_TO_READ = `Across the bottom, the confidence the model stated; up the side, how often it came good. Blue points are closes inside the range, orange points are directions right. Larger points rest on more forecasts; pale ones on fewer than ${THIN_RECORD}. On the dashed line, stated confidence matches the record: below it, the model claimed more than it delivered.`;

/** Where every figure on a page comes from, in one citable block. */
function provenance(stats, extra = []) {
  const t = stats.totals || {};
  const rows = [
    ['Source', 'ForecastNest public forecasting dataset: forecasts their author chose to publish'],
    ['Observation period', t.firstForecast && t.lastForecast ? `${day(t.firstForecast)} – ${day(t.lastForecast)}` : '—'],
    ['Counted from', stats.baselineDate ? day(stats.baselineDate) : 'the first public forecast'],
    ['Public forecasts counted', `${t.forecasts}, of which ${t.settled} settled`],
    ...(t.beforeBaseline ? [['Published but not counted', `${t.beforeBaseline}, made before the baseline`]] : []),
    ...(t.lookAhead ? [['Excluded for look-ahead', `${t.lookAhead} stock and ETF forecasts made outside market hours against the previous close, after news that close did not reflect`]] : []),
    ...extra,
    ['Methodology', '<a href="/how-scoring-works">How scoring works</a>'],
    ['License', '<a href="https://creativecommons.org/licenses/by-nc/4.0/" rel="license">CC BY-NC 4.0</a>: non-commercial reuse with credit to ForecastNest and a link to this page'],
    ['Underlying records', '<a href="/research#forecasts">Every public forecast, each on its own page</a>'],
    ['Last updated', dateTag(stats.generatedAt) || '—'],
  ];
  return `<section class="hub-provenance" id="provenance">
<h2>Citation and data provenance</h2>
<dl>
${rows.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${/^</.test(v) ? v : esc(v)}</dd>`).join('\n')}
</dl>
</section>`;
}

const COMBINED_NOTE = 'Every directional call is counted, whichever prompt it came from: the up or down of a member forecast, and each single stock a mode picked, right when the stock moved the way the model said. A mode\'s own measure — beating SPY, a long-short spread — is a different question and is not what is counted here.';

/** Each model's calls, prompt by prompt, with the combined figure first. */
function directionTable(rows) {
  const sources = [];
  for (const r of rows) for (const s of r.sources || []) if (!sources.some(x => x.id === s.id)) sources.push({ id: s.id, title: s.title });
  const cell = (r, id) => {
    const s = (r.sources || []).find(x => x.id === id);
    return s && s.calls ? ofCount(s.right / s.calls, s.right, s.calls) : '—';
  };
  // Best combined record first among the solid ones; thin records after, pale.
  const ordered = [
    ...rows.filter(r => r.graded >= THIN_RECORD).sort((a, b) => b.accuracy - a.accuracy || b.graded - a.graded),
    ...rows.filter(r => r.graded < THIN_RECORD),
  ];
  return `<div class="hub-table hub-direction-table"><table aria-labelledby="models-table-title">
${head(['Model', 'All prompts', ...sources.map(s => s.title)])}
<tbody>
${ordered.map(r => `<tr${r.graded < THIN_RECORD ? ' class="hub-thin"' : ''}><th scope="row">${esc(r.name)}${r.graded < THIN_RECORD ? ' <small>(insufficient sample)</small>' : ''}</th><td><strong>${esc(ofCount(r.accuracy, r.right, r.graded))}</strong></td>${sources.map(s => `<td>${esc(cell(r, s.id))}</td>`).join('')}</tr>`).join('\n')}
</tbody>
</table></div>`;
}

// ------------------------------------------------------------------ the hub

function researchHubHtml(stats) {
  if (!stats || !stats.totals) return '';
  const t = stats.totals;
  const bench = mainBench(stats);
  const styles = (stats.styles || []).map(s => ({ ...s, name: s.label }));
  const horizons = (stats.horizons || []).map(h => ({ ...h, name: horizonName(h.horizon) }));
  const sections = [];

  const combined = stats.directionByModel || [];
  if (combined.length) {
    sections.push({ id: 'models', title: 'Which AI calls direction best?', html: `${answer(`${rateAnswer(combined, 'model')} ${COMBINED_NOTE}`)}
${figure({ id: 'models', title: 'Direction right, by model — every prompt combined', svg: rateChart(combined, { id: 'models', title: 'Direction right, by model — every prompt combined' }), howToRead: RATE_HOW_TO_READ })}
<h3 class="hub-chart-title" id="models-table-title">Direction right, by model and prompt</h3>
${directionTable(combined)}
${more('/research/models', 'Full model research: member research by author, range accuracy and scores')}` });
  } else if (bench) {
    sections.push({ id: 'models', title: 'Which AI calls direction best?', html: `${answer(`${rateAnswer(bench.models || [], 'model')}${(stats.benches || []).length > 1 ? ` These figures are the forecasts published by ${bench.author}, the largest record here. Forecasts published by other authors are reported separately and are never pooled into this comparison.` : ''}`)}
${figure({ id: 'models', title: `Direction right, by model — ${bench.author}`, svg: rateChart(bench.models || [], { id: 'models', title: `Direction right, by model — ${bench.author}` }) })}
${more('/research/models', 'Full model research: every author, range accuracy and scores')}` });
  }
  if (styles.length) {
    sections.push({ id: 'prompts', title: 'Does the way a prompt is built make a difference?', html: `${answer(promptsAnswer(stats))}
${figure({ id: 'prompt-overview', title: 'How each prompt has done overall', svg: promptChart(stats, { id: 'prompt-overview', title: 'How each prompt has done overall' }), howToRead: PROMPT_HOW_TO_READ })}
${figure({ id: 'prompt-models', title: 'Best and weakest model within each prompt', svg: modelSpreadChart(stats, { id: 'prompt-models', title: 'Best and weakest model within each prompt' }), howToRead: MODELS_HOW_TO_READ })}
${promptTable(stats)}
${more('/research/prompts', 'Full prompt research: every prompt, and the styles within member research')}` });
  }
  if (horizons.length) {
    sections.push({ id: 'horizons', title: 'Is a short forecast easier than a long one?', html: `${answer(rateAnswer(horizons, 'horizon'))}
${figure({ id: 'horizons', title: 'Direction right, by horizon', svg: rateChart(horizons, { id: 'horizons', title: 'Direction right, by horizon' }), howToRead: RATE_HOW_TO_READ })}
${fullFigures(metricTable('Horizon', horizons, h => h.name))}` });
  }
  if ((stats.calibration || []).length) {
    sections.push({ id: 'confidence', title: 'Does stated confidence mean anything?', html: `${answer(confidenceAnswer(stats.calibration))}
${figure({ id: 'confidence', title: 'Stated confidence against outcome', svg: calibrationChart(stats.calibration, { id: 'confidence', title: 'Stated confidence against outcome' }) })}
${more('/research/confidence', 'Full confidence research: every level, and how to read it')}` });
  }
  const experiments = stats.experiments || [];
  if (experiments.length) {
    sections.push({ id: 'experiments', title: 'Which AI picks beat the benchmark?', html: `<p>Structured experiments the site runs itself: every model answers the same fixed prompt, and each run is scored once it settles. The site's own settled runs only.</p>
${experiments.map(e => {
    const scoreCol = (e.columns || []).find(c => c.format === 'signedPercent');
    const runsCol = (e.columns || []).find(c => c.format === 'count');
    const hasHorizon = (e.columns || []).some(c => c.key === 'horizon');
    const rows = scoreCol ? (e.rows || []).map(r => ({ name: `${r.model}${hasHorizon && r.horizon ? ` (${r.horizon})` : ''}`, value: r[scoreCol.key], n: runsCol ? r[runsCol.key] : 0 })) : [];
    const title = scoreCol ? `${e.title}: ${scoreCol.label}, by model` : e.title;
    return `<section class="hub-experiment" id="experiment-${esc(e.id)}">
<h3><a href="${esc(e.path)}">${esc(e.title)}</a></h3>
<p>${esc(e.description)}</p>
${rows.length ? `${answer(experimentAnswer(rows))}
${figure({ id: `exp-${e.id}`, title, svg: signedChart(rows, { id: `exp-${e.id}`, title }) })}` : '<p>No settled runs yet.</p>'}
${more(e.path, `Every ${e.title} run, its picks and its prompt`)}
</section>`;
  }).join('\n')}` });
  }
  sections.push({ id: 'method', title: 'Methodology: how these numbers are counted', html: `<ul>
<li><strong>Recorded.</strong> Every forecast is stored with the time it was made and the price it started from, before its window closes, and published forecasts keep their own page.</li>
<li><strong>Settled.</strong> When the window closes, the actual close is entered and the forecast is scored; nothing is counted before it settles.</li>
<li><strong>Direction.</strong> A forecast range pointing up or down from the starting price is a directional call. A range centred on it makes no call and is not graded on direction.</li>
<li><strong>Where the record starts.</strong> Forecasts made before the evaluation baseline${stats.baselineDate ? `, ${esc(day(stats.baselineDate))}` : ''}, are not counted: they were made under prompts with known data-quality defects. They are still published and still open on their own pages, which is why the listing can hold more forecasts than these figures count.</li>
<li><strong>Sample size.</strong> Every rate is shown with its count. A record of fewer than ${THIN_RECORD} graded calls is marked as an insufficient sample and left out of every comparison.</li>
<li><strong>Kept apart.</strong> Each author's forecasts are counted on their own and never pooled with another's: two people choosing the same model are not running the same experiment.</li>
</ul>
<p><a href="/how-scoring-works">How scoring works, in full</a>.</p>` });

  const toc = `<nav class="hub-toc" aria-label="On this page">
<h2>On this page</h2>
<ol>
${sections.map(s => `<li><a href="#${s.id}">${esc(s.title)}</a></li>`).join('\n')}
<li><a href="#provenance">Citation and data provenance</a></li>
<li><a href="#forecasts">Latest shared forecasts</a></li>
</ol>
</nav>`;

  return `<div class="research-hub">
<p class="hub-lead">Timestamped, publicly archived benchmarks of AI financial forecasting. ForecastNest compares forecasts from leading AI models with what the market then did, using fixed prompts and settlement rules set before the outcome is known.</p>
<p class="hub-meta">${esc(t.forecasts)} public forecasts · ${esc(t.settled)} settled · ${esc(plural(t.models, 'model', 'models'))} · ${esc(plural(t.assets, 'asset', 'assets'))} · ${esc(plural(t.promptGroups, 'prompt', 'prompts'))} · Updated ${dateTag(stats.generatedAt)}</p>
<section class="hub-findings" id="record" aria-label="Key findings">
<div class="hub-card"><strong>${esc(t.forecasts)}</strong><span>Public forecasts</span></div>
<div class="hub-card"><strong>${esc(t.settled)}</strong><span>Settled forecasts</span></div>
<div class="hub-card"><strong>${esc(pct(t.accuracy))}</strong><span>Direction right</span><small>${esc(t.right)} of ${esc(t.graded)} graded calls</small></div>
<div class="hub-card"><strong>${esc(pct(t.rangeCoverage))}</strong><span>Closed inside the predicted range</span><small>${esc(t.rangeHits)} of ${esc(t.rangeChecked)} settled forecasts</small></div>
</section>
<p class="hub-basis">Based on public ForecastNest forecasts${stats.baselineDate ? ` made since ${esc(day(stats.baselineDate))}` : ''} and settled through ${dateTag(stats.generatedAt)}.${t.beforeBaseline ? ` The listing below holds ${esc(t.listed)}: ${esc(t.beforeBaseline)} earlier public forecasts are still on the site, each on its own page, but are not counted in these figures.` : ''} <a href="#method">Methodology: how these numbers are counted</a>.</p>
${toc}
${sections.map(s => `<section class="hub-section" id="${s.id}">
<h2>${esc(s.title)}</h2>
${s.html}
</section>`).join('\n')}
${provenance(stats)}
</div>`;
}

// ------------------------------------------------------------------ one page per question

const TOPICS = {
  models: {
    path: '/research/models',
    title: 'Which AI Model Forecasts Market Direction Best? | ForecastNest',
    h1: 'Which AI calls market direction best?',
    description: stats => `Direction and range accuracy for ${stats.totals.models} AI models across ${stats.totals.settled} settled public forecasts, each rate with its count, per author.`,
    body(stats) {
      const benches = stats.benches || [];
      const bench = mainBench(stats);
      return `${bench ? answer(rateAnswer(bench.models || [], 'model') + (benches.length > 1 ? ` These figures are the forecasts published by ${bench.author}, the largest record here. Forecasts published by other authors are reported separately, below, and are never pooled into this comparison.` : '')) : ''}
${measures("For each model, the share of its directional calls (a forecast range pointing up or down from the starting price) that were right once the window closed, and how often the close landed inside its range. Each author's forecasts are counted on their own and never pooled with another's: two people choosing the same model are not running the same experiment.")}
${benches.map((b, i) => `<section class="hub-bench" id="author-${i + 1}">
<h2>Recorded by ${esc(b.author)}</h2>
<p>${esc(b.forecasts)} public forecasts, ${esc(b.settled)} settled. Every model here answered this author's prompts, so the comparison holds everything but the model fixed.</p>
${i ? answer(rateAnswer(b.models || [], 'model')) : ''}
${figure({ id: `models-${i}`, title: `Direction right, by model — ${b.author}`, svg: rateChart(b.models || [], { id: `models-${i}`, title: `Direction right, by model — ${b.author}` }), howToRead: RATE_HOW_TO_READ })}
${fullFigures(metricTable('Model', b.models || [], m => m.name))}
${(b.models || []).some(m => (m.horizons || []).length > 1) ? `<h3 class="hub-table-title">By horizon</h3>
<div class="hub-table"><table>
${head(['Model', 'Horizon', 'Directional calls graded', 'Direction right', 'Close inside the range'])}
<tbody>
${(b.models || []).flatMap(m => (m.horizons || []).map(h => `<tr><th scope="row">${esc(m.name)}</th><td>${esc(horizonName(h.horizon))}</td><td>${esc(h.graded)}</td><td>${esc(ofCount(h.accuracy, h.right, h.graded))}</td><td>${esc(ofCount(h.rangeCoverage, h.rangeHits, h.rangeChecked))}</td></tr>`)).join('\n')}
</tbody>
</table></div>` : ''}
</section>`).join('\n')}
${limitations([
  `Most forecasts so far look one day ahead, on ${plural(stats.totals.assets, 'asset', 'assets')}: these rates describe that task, not market forecasting in general.`,
  'A model is recorded from its own answer and checked against the other answers to the same prompt; an answer found filed under the wrong model is corrected by hand.',
  `A record of fewer than ${THIN_RECORD} graded calls is an insufficient sample: one forecast can move it a long way.`,
])}`;
    },
  },
  prompts: {
    path: '/research/prompts',
    title: 'Does Prompt Design Affect AI Market Forecasts? | ForecastNest',
    h1: 'Does the way a prompt is built make a difference?',
    description: stats => `AI market forecasts grouped by how the prompt was built, with direction and range accuracy for each style across ${stats.totals.settled} settled public forecasts.`,
    body(stats) {
      const styles = (stats.styles || []).map(s => ({ ...s, name: s.label }));
      const describe = s => (s.carriesData === null ? 'Not recorded for these forecasts.'
        : `${s.carriesData ? 'Supplies a market data package.' : 'Supplies no market data; the model researches the asset itself.'} ${s.imposesMethod ? 'Imposes a method.' : 'Leaves the reasoning to the model.'}`);
      return `${answer(promptsAnswer(stats))}
${figure({ id: 'prompt-overview', title: 'How each prompt has done overall', svg: promptChart(stats, { id: 'prompt-overview', title: 'How each prompt has done overall' }), howToRead: PROMPT_HOW_TO_READ })}
${figure({ id: 'prompt-models', title: 'Best and weakest model within each prompt', svg: modelSpreadChart(stats, { id: 'prompt-models', title: 'Best and weakest model within each prompt' }), howToRead: MODELS_HOW_TO_READ })}
${promptTable(stats)}
<h2>Within member research, by prompt style</h2>
<p>${esc(rateAnswer(styles, 'prompt style'))}</p>
${measures('The same direction and range measures, grouped by how the prompt was built: whether it supplied market data, and whether it imposed a method. Pooled across authors: here the prompt is the subject, and the model is not.')}
${figure({ id: 'prompts', title: 'Direction right, by prompt style', svg: rateChart(styles, { id: 'prompts', title: 'Direction right, by prompt style' }), howToRead: RATE_HOW_TO_READ })}
${fullFigures(metricTable('Prompt style', styles, s => s.name))}
<h2>The prompt styles</h2>
<dl class="hub-styles">${styles.map(s => `<dt>${esc(s.name)}</dt><dd>${esc(describe(s))}</dd>`).join('')}</dl>
${limitations([
  'The prompts ask different questions and are scored on different measures: a share of directions called right and a share of picks that beat SPY are not the same number, so no prompt is ranked above another here.',
  'Prompt styles were chosen by the people running the forecasts, not assigned at random, so a difference between styles can come from what each was used for.',
  `A style with fewer than ${THIN_RECORD} graded calls is an insufficient sample and is not compared.`,
])}`;
    },
  },
  confidence: {
    path: '/research/confidence',
    title: 'Does an AI\'s Stated Confidence Predict Its Accuracy? | ForecastNest',
    h1: 'Does stated confidence mean anything?',
    description: stats => `Settled AI market forecasts grouped by the confidence each model stated, against how often the direction was right and the close landed in range: ${stats.totals.settled} forecasts.`,
    body(stats) {
      const c = stats.calibration || [];
      return `${answer(confidenceAnswer(c))}
${measures('Every forecast states a confidence. This groups the settled forecasts by that number and asks how often they came good: did the close land inside the stated range, and was the direction right? A forecaster whose confidence means something is right more often when it says it is surer.')}
${figure({ id: 'confidence', title: 'Stated confidence against outcome', svg: calibrationChart(c, { id: 'confidence', title: 'Stated confidence against outcome' }), howToRead: CALIBRATION_HOW_TO_READ })}
${fullFigures(`<div class="hub-table"><table>
${head(['Stated confidence', 'Settled', 'Close inside the range', 'Directional calls graded', 'Direction right'])}
<tbody>
${c.map(r => `<tr><th scope="row">${esc(r.bucket)}%${r.graded < THIN_RECORD ? ' <small>(insufficient sample)</small>' : ''}</th><td>${esc(r.settled)}</td><td>${esc(ofCount(r.rangeCoverage, r.rangeHits, r.rangeChecked))}</td><td>${esc(r.graded)}</td><td>${esc(ofCount(r.accuracy, r.right, r.graded))}</td></tr>`).join('\n')}
</tbody>
</table></div>`)}
${limitations([
  'The confidence is the model\'s own number, stated in its answer; models do not all mean the same thing by it.',
  'All models are pooled here, so a level can be dominated by whichever model states it most often.',
  `A level with fewer than ${THIN_RECORD} graded calls is an insufficient sample and is left out of the short answer.`,
])}`;
    },
  },
};

/** One research question's own page: { title, description, h1, html }. */
function researchTopicPage(topic, stats) {
  const def = TOPICS[topic];
  if (!def || !stats || !stats.totals) return null;
  return {
    path: def.path,
    title: def.title,
    description: def.description(stats),
    h1: def.h1,
    html: `<div class="research-hub">
<p class="hub-meta"><a href="/research">AI Forecasting Research</a> · Updated ${dateTag(stats.generatedAt)}</p>
${def.body(stats)}
${provenance(stats)}
<p class="hub-more"><a href="/research">← All research questions</a></p>
</div>`,
  };
}

module.exports = {
  researchHubHtml, researchTopicPage, TOPICS,
  rateChart, calibrationChart, signedChart,
  rateAnswer, confidenceAnswer, experimentAnswer, provenance, day,
  THIN_RECORD,
};
